import { useId, useMemo } from "react";
import { buildGeometry, type LayerState, type Shape } from "./geometry";
import type { FloorPlan as FloorPlanRecord } from "@/types";

interface FloorPlanProps {
  plan: FloorPlanRecord;
  locked: boolean;
  layers?: Partial<LayerState>;
  /** Thumbnails drop all annotation and margin. */
  thumb?: boolean;
  className?: string;
}

function ShapeNode({ shape }: { shape: Shape }) {
  if (shape.kind === "circle") {
    return <circle cx={shape.x} cy={shape.y} r={shape.r} />;
  }
  return <rect x={shape.x} y={shape.y} width={shape.w} height={shape.h} rx={shape.rx} />;
}

/**
 * Renders CAD linework as real SVG elements from pure geometry.
 *
 * The locked drawing is a different drawing, not a hidden one — see
 * geometry.ts. Nothing here re-adds withheld data.
 */
export function FloorPlanDrawing({ plan, locked, layers, thumb = false, className }: FloorPlanProps) {
  const uid = useId().replace(/:/g, "");
  const geometry = useMemo(
    () => buildGeometry(plan, { locked, layers, thumb }),
    [plan, locked, layers, thumb],
  );

  const showGrid = (layers?.grid ?? true) && !thumb;
  const margin = thumb ? 3 : 13;
  const { width: W, depth: D } = geometry;
  const viewBox = `${-margin} ${-margin} ${W + margin * 2} ${D + margin * 2}`;

  const hatchId = `fp-hatch-${uid}`;
  const gridId = `fp-grid-${uid}`;

  return (
    <svg
      className={`fp-svg${className ? ` ${className}` : ""}`}
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={
        locked
          ? `Locked preview of ${plan.name}. ${geometry.rooms.length - 1} of ${geometry.rooms.length} rooms withheld until licensed.`
          : `${plan.name} floor plan, level 1`
      }
    >
      <defs>
        <pattern
          id={hatchId}
          width={1.6}
          height={1.6}
          patternUnits="userSpaceOnUse"
          patternTransform="rotate(45)"
        >
          <rect width={1.6} height={1.6} fill="#e4ebe1" />
          <line x1={0} y1={0} x2={0} y2={1.6} stroke="#9fb39c" strokeWidth={0.45} />
        </pattern>

        <pattern id={gridId} width={5} height={5} patternUnits="userSpaceOnUse">
          <path d="M5 0H0V5" fill="none" stroke="#2e5a32" strokeOpacity={0.13} strokeWidth={0.12} />
        </pattern>
      </defs>

      {showGrid ? (
        <rect
          x={-margin}
          y={-margin}
          width={W + margin * 2}
          height={D + margin * 2}
          fill={`url(#${gridId})`}
        />
      ) : null}

      {/* Structure: envelope filled with wall hatch, rooms punched out of it. */}
      <g className="fp-walls">
        <rect x={0} y={0} width={W} height={D} fill={`url(#${hatchId})`} stroke="#1f2a22" strokeWidth={0.28} />
        {geometry.rooms.map((room) => (
          <rect
            key={room.index}
            x={room.box.x}
            y={room.box.y}
            width={room.box.w}
            height={room.box.d}
            fill="#ffffff"
            stroke="#1f2a22"
            strokeWidth={0.14}
          />
        ))}
      </g>

      {geometry.doors.length || geometry.windows.length ? (
        <g className="fp-open">
          {geometry.windows.map((win, i) => (
            <g key={`w${i}`}>
              <rect x={win.cut.x} y={win.cut.y} width={win.cut.w} height={win.cut.d} fill="#fff" />
              {win.lines.map((line, j) => (
                <line key={j} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} />
              ))}
            </g>
          ))}
          {geometry.doors.map((door, i) => (
            <g key={`d${i}`}>
              <rect x={door.cut.x} y={door.cut.y} width={door.cut.w} height={door.cut.d} fill="#fff" />
              <g className="fp-door">
                <path d={door.path} />
              </g>
            </g>
          ))}
        </g>
      ) : null}

      {geometry.furniture.map((group) => (
        <g key={`f${group.roomIndex}`} className="fp-furn">
          {group.shapes.map((shape, i) => (
            <ShapeNode key={i} shape={shape} />
          ))}
        </g>
      ))}

      {geometry.electrical.map((group) => (
        <g key={`e${group.roomIndex}`} className="fp-elec">
          {group.shapes.map((shape, i) => (
            <ShapeNode key={i} shape={shape} />
          ))}
          {group.lines.map((line, i) => (
            <line key={i} x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2} />
          ))}
        </g>
      ))}

      {geometry.plumbing.map((group) => (
        <g key={`p${group.roomIndex}`} className="fp-plumb">
          <path d={group.path} />
          {group.shapes.map((shape, i) => (
            <ShapeNode key={i} shape={shape} />
          ))}
        </g>
      ))}

      {/* Labels. A withheld room gets a bare number and nothing else. */}
      <g className="fp-labels">
        {geometry.rooms.map((room) => {
          const cx = room.box.x + room.box.w / 2;
          const cy = room.box.y + room.box.d / 2;

          if (!room.name) {
            if (thumb || room.box.w <= 5 || room.box.d <= 5) return null;
            return (
              <text key={room.index} className="fp-redacted" x={cx} y={cy + 0.6} textAnchor="middle">
                {room.index + 1}
              </text>
            );
          }

          return (
            <g key={room.index}>
              <text className="fp-room" x={cx} y={cy - 0.4} textAnchor="middle">
                {room.name.toUpperCase()}
              </text>
              <text className="fp-area" x={cx} y={cy + 2.2} textAnchor="middle">
                {room.dimensions}
              </text>
            </g>
          );
        })}
      </g>

      {/* Sample room frame, shown only on a locked preview. */}
      {geometry.rooms
        .filter((room) => room.isSample && !thumb)
        .map((room) => (
          <g key={`s${room.index}`} className="fp-sample">
            <rect
              x={room.box.x - 0.5}
              y={room.box.y - 0.5}
              width={room.box.w + 1}
              height={room.box.d + 1}
              fill="none"
              stroke="#2e5a32"
              strokeWidth={0.35}
              strokeDasharray="1.6 1.1"
            />
            <text
              className="fp-sample-tag"
              x={room.box.x + room.box.w / 2}
              y={room.box.y - 1.6}
              textAnchor="middle"
            >
              SAMPLE ROOM — UNLOCKED
            </text>
          </g>
        ))}

      {geometry.dimensions.length ? (
        <g className="fp-dim">
          {geometry.dimensions.map((dim, i) => {
            const mx = (dim.x1 + dim.x2) / 2;
            const my = (dim.y1 + dim.y2) / 2;
            const tick = 1.1;

            return (
              <g key={i}>
                <line x1={dim.x1} y1={dim.y1} x2={dim.x2} y2={dim.y2} />
                {dim.vertical ? (
                  <>
                    <line x1={dim.x1 - tick} y1={dim.y1} x2={dim.x1 + tick} y2={dim.y1} />
                    <line x1={dim.x2 - tick} y1={dim.y2} x2={dim.x2 + tick} y2={dim.y2} />
                    <text
                      x={mx - 2.2}
                      y={my}
                      textAnchor="middle"
                      transform={`rotate(-90 ${mx - 2.2} ${my})`}
                    >
                      {dim.text}
                    </text>
                  </>
                ) : (
                  <>
                    <line x1={dim.x1} y1={dim.y1 - tick} x2={dim.x1} y2={dim.y1 + tick} />
                    <line x1={dim.x2} y1={dim.y2 - tick} x2={dim.x2} y2={dim.y2 + tick} />
                    <text x={mx} y={my - 1.6} textAnchor="middle">
                      {dim.text}
                    </text>
                  </>
                )}
              </g>
            );
          })}
        </g>
      ) : null}

      {/* North arrow, scale bar and title block. */}
      {thumb ? null : (
        <g className="fp-anno">
          <circle cx={W + 6} cy={4} r={4} fill="none" strokeWidth={0.2} />
          <path
            d={`M${W + 6} ${0.8} L${W + 7.5} ${6.2} L${W + 6} ${4.9} L${W + 4.5} ${6.2} Z`}
            fill="#1f2a22"
          />
          <text className="fp-anno-t" x={W + 6} y={-1} textAnchor="middle">
            N
          </text>

          <g className="fp-scale">
            {[0, 1, 2, 3].map((i) => (
              <rect
                key={i}
                x={i * 5}
                y={D + 7}
                width={5}
                height={1.1}
                fill={i % 2 ? "#fff" : "#1f2a22"}
                stroke="#1f2a22"
                strokeWidth={0.12}
              />
            ))}
            <text className="fp-anno-t" x={0} y={D + 10.6}>
              0
            </text>
            <text className="fp-anno-t" x={20} y={D + 10.6} textAnchor="middle">
              20 ft
            </text>
          </g>

          <text className="fp-title" x={W} y={D + 8.4} textAnchor="end">
            {plan.code}
            {locked ? "  ·  PREVIEW — NOT FOR CONSTRUCTION" : `  ·  ${plan.name.toUpperCase()}`}
          </text>
          <text className="fp-anno-t" x={W} y={D + 11.4} textAnchor="end">
            SCALE 1:100 · ALL DIMENSIONS IN FEET · LEVEL 01
          </text>
        </g>
      )}
    </svg>
  );
}
